/**
 * My Stores Tab - Manage your stores
 */
import { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react'
import { useAuth } from '@trafiflow/shared-ui'
import { Button } from '@trafiflow/shared-ui'
import { Plus, Edit, Trash2, Store as StoreIcon, ExternalLink } from 'lucide-react'
import apiClient from '../../../../shared/lib/api'
import type { Store, Brand } from '../../types'
import StoreModal from '../../components/StoreModal'

export interface MyStoresTabRef {
  refresh: () => void
}

const MyStoresTab = forwardRef<MyStoresTabRef>((_props, ref) => {
  const { user } = useAuth()
  const [stores, setStores] = useState<Store[]>([])
  const [brands, setBrands] = useState<Brand[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingStore, setEditingStore] = useState<Store | null>(null)
  const [selectedBrandId, setSelectedBrandId] = useState<string>('')

  const fetchBrands = useCallback(async () => {
    if (!user?.tenantId) return

    try {
      const response = await apiClient.get('/brands')
      setBrands(response.data.brands || [])
    } catch (err: unknown) {
      console.error('Error fetching brands:', err)
    }
  }, [user?.tenantId])

  const fetchStores = useCallback(async (brandId?: string) => {
    if (!user?.tenantId) return

    try {
      setLoading(true)
      setError(null)
      const url = brandId ? `/stores?brandId=${brandId}` : '/stores'
      const response = await apiClient.get(url)
      setStores(response.data.stores || [])
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { message?: string } } }
      if (error.response?.status === 404) {
        setStores([])
        setError(null)
      } else {
        console.error('Error fetching stores:', err)
        setError(error.response?.data?.message || 'Failed to load stores')
        setStores([])
      }
    } finally {
      setLoading(false)
    }
  }, [user?.tenantId])

  useEffect(() => {
    fetchBrands()
    fetchStores()
  }, [fetchBrands, fetchStores])

  useEffect(() => {
    if (selectedBrandId) {
      fetchStores(selectedBrandId)
    } else {
      fetchStores()
    }
  }, [selectedBrandId, fetchStores])

  useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchBrands()
      fetchStores(selectedBrandId || undefined)
    }
  }))

  const handleCreate = () => {
    setEditingStore(null)
    setIsModalOpen(true)
  }

  const handleEdit = (store: Store) => {
    setEditingStore(store)
    setIsModalOpen(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this store?')) {
      return
    }

    try {
      await apiClient.delete(`/stores/${id}`)
      await fetchStores(selectedBrandId || undefined)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } }
      console.error('Error deleting store:', err)
      alert(error.response?.data?.message || 'Failed to delete store')
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditingStore(null)
  }

  const handleModalSave = async () => {
    await fetchStores(selectedBrandId || undefined)
    handleModalClose()
  }

  const getBrandName = (brandId: string) => {
    const brand = brands.find((b) => b.id === brandId)
    return brand?.name || brandId
  }

  if (loading && stores.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-10 h-10 border-2 border-tf-border border-t-primary rounded-full animate-spin mb-4" />
        <p className="text-sm font-medium text-tf-text-sub">Loading stores...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-tf-text">Stores</h2>
          <p className="text-sm text-tf-text-muted mt-0.5">
            {stores.length} {stores.length === 1 ? 'store' : 'stores'} registered
          </p>
        </div>
        <div className="flex items-center gap-3">
          {brands.length > 0 && (
            <select
              id="brandFilter"
              value={selectedBrandId}
              onChange={(e) => setSelectedBrandId(e.target.value)}
              className="text-sm border border-tf-border rounded-lg px-3 py-2 bg-tf-bg-card focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">All brands</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          )}
          <Button
            variant="primary"
            onClick={handleCreate}
            icon={<Plus className="w-4 h-4" />}
            disabled={brands.length === 0}
          >
            Add Store
          </Button>
        </div>
      </div>

      {brands.length === 0 && (
        <div className="rounded-lg bg-tf-feedback-warning-bg border border-tf-feedback-warning-border px-4 py-3 text-sm text-tf-feedback-warning-text">
          Create a brand first before adding stores.
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-tf-feedback-error-bg border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {stores.length === 0 ? (
        <div className="text-center py-16 rounded-xl border-2 border-dashed border-tf-border bg-tf-bg-muted">
          <div className="w-14 h-14 rounded-xl bg-tf-bg-hover flex items-center justify-center mx-auto mb-4">
            <StoreIcon className="w-7 h-7 text-tf-text-muted" />
          </div>
          <h3 className="text-base font-semibold text-tf-text mb-1">No stores yet</h3>
          <p className="text-sm text-tf-text-muted mb-6 max-w-sm mx-auto">
            Add your first store to connect it with your brand
          </p>
          <Button
            variant="primary"
            onClick={handleCreate}
            icon={<Plus className="w-4 h-4" />}
            disabled={brands.length === 0}
          >
            Add Store
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2 rounded-lg border border-tf-border">
          <table className="min-w-full">
            <thead>
              <tr className="bg-tf-bg-muted border-b border-tf-border">
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider">
                  Store
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider">
                  Brand
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider">
                  URL
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider hidden xl:table-cell">
                  Entity ID
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider hidden md:table-cell">
                  Created
                </th>
                <th className="px-5 py-3.5 text-right text-xs font-semibold text-tf-text-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-tf-border-sub">
              {stores.map((store) => (
                <tr key={store.id} className="bg-tf-bg-card hover:bg-tf-bg-muted transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      {store.logoUrl ? (
                        <img
                          src={store.logoUrl}
                          alt={store.name}
                          className="w-9 h-9 rounded-lg object-cover ring-1 ring-tf-border"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-tf-bg-hover flex items-center justify-center">
                          <StoreIcon className="w-4 h-4 text-tf-text-muted" />
                        </div>
                      )}
                      <span className="font-medium text-tf-text">{store.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm text-tf-text-sub">{getBrandName(store.brandId)}</span>
                  </td>
                  <td className="px-5 py-4">
                    <a
                      href={store.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                    >
                      <span className="truncate max-w-[160px] block">{store.url}</span>
                      <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                    </a>
                  </td>
                  <td className="px-5 py-4 hidden xl:table-cell">
                    <code className="text-xs font-mono text-tf-text-muted bg-tf-bg-muted px-2 py-1 rounded">
                      {store.agentplatEntityId ? `${store.agentplatEntityId.slice(0, 12)}…` : '—'}
                    </code>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell text-sm text-tf-text-muted">
                    {new Date(store.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => handleEdit(store)}
                        className="p-2 text-tf-text-muted hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(store.id)}
                        className="p-2 text-tf-text-muted hover:text-red-600 hover:bg-tf-feedback-error-bg rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <StoreModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        store={editingStore}
        brands={brands}
      />
    </div>
  )
})

MyStoresTab.displayName = 'MyStoresTab'

export default MyStoresTab
